import { useState } from 'react';
import { useDeepSignal } from '../context';
import { personaMetricCols } from '../constants';
import { buildPersonaRowsFromApi } from '../builders';
import { SectionLabel, SectionTitle, DSLoadingRow, DSEmptyState } from '../components/section-primitives';
import { VariableHeatmapRows } from '../components/variable-heatmap-rows';

export function PersonasSection() {
  const { apiData, loading } = useDeepSignal();
  const [expandedPersona, setExpandedPersona] = useState<string | null>(null);

  if (loading) return (
    <div style={{ padding: '32px 64px' }}>
      {[1,2,3,4].map(i => <DSLoadingRow key={i} />)}
    </div>
  );
  if (!apiData) return <DSEmptyState message="No persona data available." />;

  const pRows = buildPersonaRowsFromApi(apiData);

  if (pRows.length === 0) return <DSEmptyState message="No persona data available. Add persona definitions to the customer framework to enable this view." />;

  return (
    <div style={{ padding: '32px 64px 48px' }}>
      <div style={{ marginBottom: '24px' }}>
        <SectionLabel>Buyer Personas</SectionLabel>
        <SectionTitle size="xl">Recommendation outcomes by persona type.</SectionTitle>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', marginTop: '4px', lineHeight: '1.5' }}>
          How AI models perform across the recommendation funnel for each buyer type.
        </p>
      </div>

      {/* Column headers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '260px 1fr 1fr 1fr 1fr 40px',
        gap: '0',
        padding: '0 20px 10px',
        borderBottom: '1px solid var(--border)',
      }}>
        <div />
        {personaMetricCols.map(col => (
          <div key={col.key} style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            color: 'var(--muted-foreground)',
            letterSpacing: '0.2px',
            textAlign: 'center',
          }}>
            {col.label}
          </div>
        ))}
        <div />
      </div>

      {/* Persona rows */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {pRows.map((p, i) => (
          <div key={p.name} style={{ marginTop: i === 0 ? '0' : '8px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '260px 1fr 1fr 1fr 1fr',
                gap: '0',
                alignItems: 'center',
                padding: '16px 24px 16px 30px',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                backgroundColor: 'var(--background)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Teal left band */}
              <div style={{
                position: 'absolute',
                left: 0, top: 0, bottom: 0,
                width: '4px',
                backgroundColor: '#00c9c4',
                borderRadius: '10px 0 0 10px',
              }} />

              {/* Persona identity */}
              <div style={{ paddingRight: '24px' }}>
                <p style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--foreground)', marginBottom: '2px' }}>{p.name}</p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', marginBottom: '4px' }}>{p.role}</p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', lineHeight: '1.4' }}>{p.decisionFrame}</p>
              </div>

              {/* Mention Rate */}
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--foreground)', lineHeight: 1, marginBottom: '3px' }}>{p.mention}</p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>Mention Rate</p>
              </div>

              {/* Top-5 Rate */}
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--foreground)', lineHeight: 1, marginBottom: '3px' }}>{p.top5}</p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>Top-5 Rate</p>
              </div>

              {/* Primary Rec */}
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: p.primaryRecColor, lineHeight: 1, marginBottom: '3px' }}>{p.primaryRec}</p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>Primary Rec</p>
              </div>

              {/* Best Model */}
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', marginBottom: '3px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: p.bestModelColor, flexShrink: 0 }} />
                  <p style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--foreground)', lineHeight: 1 }}>{p.bestModel}</p>
                </div>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>{p.bestModelRate} primary rec</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Variable Breakdown — separate section below the table */}
      <div style={{ marginTop: '40px' }}>
        <div style={{ marginBottom: '20px' }}>
          <SectionLabel>Brand Attribute Breakdown</SectionLabel>
          <SectionTitle size="xl">Encoding strength per reasoning variable, by persona.</SectionTitle>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {pRows.map((p) => {
            const isExpanded = expandedPersona === p.name;
            // Build per-persona variable list from this persona's variable_scores
            const vars = apiData.variables
              .map(v => {
                const shortName = v.claim.includes(':') ? v.claim.split(':')[0].trim() : v.claim;
                return { name: shortName, score: p.variableScores[v.id] ?? 0 };
              })
              .sort((a, b) => b.score - a.score);
            const topVar = vars[0]?.name;
            const bottomVar = vars[vars.length - 1]?.name;

            return (
              <div key={p.name} style={{ border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden', backgroundColor: 'var(--background)' }}>
                {/* Collapsible header */}
                <button
                  onClick={() => setExpandedPersona(isExpanded ? null : p.name)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 20px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    borderBottom: isExpanded ? '1px solid var(--border)' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '4px', height: '18px', borderRadius: '2px', backgroundColor: '#00c9c4', flexShrink: 0 }} />
                    <span style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--foreground)' }}>{p.name}</span>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>{p.role}</span>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: 'var(--muted-foreground)', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }}>
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {/* Expanded heatmap */}
                {isExpanded && (
                  <div style={{ padding: '20px 24px 24px' }}>
                    <VariableHeatmapRows vars={vars} topVar={topVar} bottomVar={bottomVar} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
